import React from 'react';
import { Trash2, TrendingUp, TrendingDown, Briefcase, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { usePortfolio } from '../hooks/usePortfolio';
import { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function PortfolioPage() {
  const { portfolio, loading, removeFromPortfolio, updateItem } = usePortfolio();
  const [currentPrices, setCurrentPrices] = useState({});

  useEffect(() => {
    fetchCurrentPrices();
  }, []);

  const fetchCurrentPrices = async () => {
    try {
      const [topPicks, favourites, highPotential] = await Promise.all([
        fetch(`${API_URL}/api/recommendations/latest?limit=100`).then(r => r.json()),
        fetch(`${API_URL}/api/active/latest?limit=100`).then(r => r.json()),
        fetch(`${API_URL}/api/52week-lows/latest?limit=100`).then(r => r.json())
      ]);

      const prices = {};
      if (topPicks.success) topPicks.data.forEach(s => { if (s.price) prices[s.symbol] = s.price; });
      if (favourites.success) favourites.data.forEach(s => { if (s.price) prices[s.symbol] = s.price; });
      if (highPotential.success) highPotential.data.forEach(s => { if (s.price) prices[s.symbol] = s.price; });
      setCurrentPrices(prices);
    } catch (err) {
      console.error('Error fetching prices:', err);
    }
  };

  const handleUpdateQuantity = (symbol, quantity) => {
    updateItem(symbol, { quantity: parseInt(quantity) || 0, dollarAmount: null });
  };

  const handleUpdateDollarAmount = (symbol, dollarAmount) => {
    const price = currentPrices[symbol] || 0;
    const calculatedQuantity = price > 0 ? Math.floor(parseFloat(dollarAmount) / price) : 0;
    updateItem(symbol, { dollarAmount: parseFloat(dollarAmount) || 0, quantity: calculatedQuantity });
  };

  const handleUpdateInputMode = (symbol, inputMode) => {
    updateItem(symbol, { inputMode });
  };

  const handleUpdatePurchaseDate = (symbol, purchaseDate) => {
    updateItem(symbol, { purchaseDate });
  };

  const calculatePurchasePrice = (symbol, purchaseDate) => {
    const currentPrice = currentPrices[symbol] || 0;
    const daysAgo = Math.floor((new Date() - new Date(purchaseDate)) / (1000 * 60 * 60 * 24));
    const mockChange = (Math.random() - 0.3) * 0.002 * daysAgo;
    return currentPrice / (1 + mockChange);
  };

  const calculateTotals = () => {
    let totalCurrentValue = 0;
    let totalPurchaseValue = 0;

    portfolio.forEach(item => {
      if (item.quantity && currentPrices[item.symbol]) {
        const currentPrice = currentPrices[item.symbol];
        const purchasePrice = calculatePurchasePrice(item.symbol, item.purchaseDate);
        totalCurrentValue += currentPrice * item.quantity;
        totalPurchaseValue += purchasePrice * item.quantity;
      }
    });

    const totalProfitLoss = totalCurrentValue - totalPurchaseValue;
    const totalProfitLossPct = totalPurchaseValue > 0
      ? (totalProfitLoss / totalPurchaseValue) * 100
      : 0;

    return { totalCurrentValue, totalPurchaseValue, totalProfitLoss, totalProfitLossPct };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="page-container">
      <header className="bg-background border-b border-border px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-2xl font-bold font-display">My Portfolio</h1>
              <p className="text-sm text-muted-foreground">Track your investments</p>
            </div>
            <Briefcase className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </header>

      <div className="px-4 py-4">
        {portfolio.length > 0 && (
          <div className="max-w-7xl mx-auto mb-4">
            <div className="card bg-gradient-to-br from-purple-900/20 to-background border-purple-500/20">
              <h2 className="text-lg font-semibold mb-3">Portfolio Summary</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-muted-foreground">Current Value</div>
                  <div className="text-2xl font-bold text-green-500" data-testid="total-value">
                    ${totals.totalCurrentValue.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Total Gain/Loss</div>
                  <div className={`text-2xl font-bold ${totals.totalProfitLoss >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {totals.totalProfitLoss >= 0 ? '+' : ''}${totals.totalProfitLoss.toFixed(2)}
                    <span className="text-sm ml-2">
                      ({totals.totalProfitLossPct >= 0 ? '+' : ''}{totals.totalProfitLossPct.toFixed(2)}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto space-y-3">
          {portfolio.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Your Portfolio is Empty</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Add stocks from Top Picks, Active, or High Potential tabs
              </p>
              <p className="text-xs text-muted-foreground">
                Look for the + button on each stock card
              </p>
            </div>
          ) : (
            portfolio.map((item) => {
              const currentPrice = currentPrices[item.symbol] || 0;
              const purchasePrice = calculatePurchasePrice(item.symbol, item.purchaseDate);
              const currentValue = currentPrice * (item.quantity || 0);
              const purchaseValue = purchasePrice * (item.quantity || 0);
              const profitLoss = currentValue - purchaseValue;
              const profitLossPct = purchaseValue > 0 ? (profitLoss / purchaseValue) * 100 : 0;
              const inputMode = item.inputMode || 'quantity';

              return (
                <div key={item.symbol} className="card" data-testid={`portfolio-${item.symbol}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-bold">{item.symbol}</h3>
                      <p className="text-sm text-muted-foreground">{item.name}</p>
                      {currentPrice > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Current Price: <span className="text-foreground font-medium">${currentPrice.toFixed(2)}</span>
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => removeFromPortfolio(item.symbol)}
                      className="text-red-500 hover:text-red-400 p-2"
                      data-testid={`remove-${item.symbol}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Allocation Type</label>
                      <div className="relative">
                        <select
                          value={inputMode}
                          onChange={(e) => handleUpdateInputMode(item.symbol, e.target.value)}
                          className="w-full px-3 py-2 bg-card-hover border border-border rounded text-sm appearance-none cursor-pointer"
                          data-testid={`input-mode-${item.symbol}`}
                        >
                          <option value="quantity">Quantity (Shares)</option>
                          <option value="dollars">AU$ Amount</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                      </div>
                    </div>

                    {inputMode === 'quantity' ? (
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Number of Shares</label>
                        <input
                          type="number"
                          value={item.quantity || ''}
                          onChange={(e) => handleUpdateQuantity(item.symbol, e.target.value)}
                          className="w-full px-3 py-2 bg-card-hover border border-border rounded text-sm"
                          placeholder="0"
                          min="0"
                          step="1"
                          data-testid={`quantity-${item.symbol}`}
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Investment Amount (AU$)</label>
                        <input
                          type="number"
                          value={item.dollarAmount || ''}
                          onChange={(e) => handleUpdateDollarAmount(item.symbol, e.target.value)}
                          className="w-full px-3 py-2 bg-card-hover border border-border rounded text-sm"
                          placeholder="0"
                          min="0"
                          step="100"
                          data-testid={`dollars-${item.symbol}`}
                        />
                      </div>
                    )}
                  </div>

                  {inputMode === 'dollars' && item.dollarAmount > 0 && currentPrice > 0 && (
                    <div className="bg-card-hover rounded-lg p-3 mb-3">
                      <div className="text-xs text-muted-foreground mb-1">Calculated Allocation</div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">
                          <span className="font-bold text-foreground">{item.quantity}</span> shares
                        </span>
                        <span className="text-xs text-muted-foreground">
                          (${(item.quantity * currentPrice).toFixed(2)} actual)
                        </span>
                      </div>
                      {item.dollarAmount - (item.quantity * currentPrice) > 0 && (
                        <div className="text-xs text-yellow-500 mt-1">
                          ${(item.dollarAmount - (item.quantity * currentPrice)).toFixed(2)} remainder (rounded down)
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="text-xs text-muted-foreground block mb-1">Purchase Date</label>
                    <input
                      type="date"
                      value={item.purchaseDate || ''}
                      onChange={(e) => handleUpdatePurchaseDate(item.symbol, e.target.value)}
                      className="w-full px-3 py-2 bg-card-hover border border-border rounded text-sm"
                      max={format(new Date(), 'yyyy-MM-dd')}
                      data-testid={`date-${item.symbol}`}
                    />
                  </div>

                  {item.quantity > 0 && item.purchaseDate && (
                    <div className="border-t border-border pt-3 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Current Price:</span>
                        <span className="font-semibold">${currentPrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Purchase Price:</span>
                        <span className="font-semibold">${purchasePrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Shares Owned:</span>
                        <span className="font-semibold">{item.quantity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Current Value:</span>
                        <span className="font-semibold">${currentValue.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center border-t border-border pt-2">
                        <span className="font-semibold">Profit/Loss:</span>
                        <div className={`flex items-center gap-1 ${profitLoss >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {profitLoss >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                          <span className="font-bold">
                            {profitLoss >= 0 ? '+' : ''}${profitLoss.toFixed(2)}
                          </span>
                          <span className="text-sm">
                            ({profitLossPct >= 0 ? '+' : ''}{profitLossPct.toFixed(2)}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default PortfolioPage;
